import React, { useEffect, useRef, useState } from "react";
import Stepper from "bs-stepper";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "bs-stepper/dist/css/bs-stepper.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "admin-lte/dist/css/adminlte.min.css";
import { useNavigate } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import { useLocation } from "react-router-dom";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import FormLabel from "../../shared/helpers/FormLabel";
import { US_STATES } from "../../shared/helpers/constants";
import { formatZipCode } from "../../shared/helpers/formUtils";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import Select from "react-select";
import { useLocalization } from "../../context/LocalizationContext";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import { UserRole } from "../../shared/Enums/UserRole";

const CreateCustomerPage = () => {
  const { t } = useLocalization();
  const [initialCustomerValues, setInitialCustomerValues] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address1: "",
    address2: "",
    address3: "",
    cityOrLocality: "",
    stateOrProvinceCode: "",
    postalCodeOrZip: "",
    smsConsent: false,
  });
  const [vinError, setVinError] = useState(false);
  const [availableCities, setAvailableCities] = useState([]);
  const [initialVehicleValues, setInitialVehicleValues] = useState({
    year: "",
    make: "",
    model: "",
    submodel: "",
    vin: "",
  });
  const [formData, setFormData] = useState({
    customer: null,
    vehicle: null,
  });
  const stepperRef = useRef(null);
  const stepper = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [submodels, setSubModels] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedMakeId, setSelectedMakeId] = useState("");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedSubModelId, setSelectedSubModelId] = useState("");
  const [vehicleEditData, setVehicleEditData] = useState();
  const [isVinMode, setIsVinMode] = useState(false);
  const [isCustomerEditMode, setIsCustomerEditMode] = useState(false);
  const [isVehicleEditMode, setIsVehicleEditMode] = useState(false);
  const [hasVehicleChanges, setHasVehicleChanges] = useState(false);
  const [customerEditData, setCustomerEditData] = useState();
  const [customerId, setCustomerId] = useState();
  const [vehicleId, setVehicleId] = useState();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedDealershipId] = useSelectedDealership();
  const tenantId = localStorage.getItem("tenantId");
  const promises = [];

  useEffect(() => {
    stepper.current = new Stepper(stepperRef.current, {
      linear: true,
      animation: true,
    });
    const state = location.state || {};
    const { vehicleId, customerId } = location.state || {};
    const { customer } = location.state || {};
    setCustomerEditData(customer);
    setCustomerId(customerId);
    setVehicleId(vehicleId);
    if (state.goToCustomerStep && customerId) {
      setIsCustomerEditMode(true);
      setIsVehicleEditMode(true);
    }
    if (state.goToCustomerStep || state.goToVehicleStep) {
      if (state.goToCustomerStep) {
        setTimeout(() => {
          stepper.current.to(1);
        });
      }
      if (customerId) {
        const idData = {
          id: customerId,
        };
        showLoader();
        promises.push(
          apiManager
            .post(API_ENDPOINTS.CUSTOMER_GET_BY_ID, idData, {})
            .then((res) => {
              if (res.status == 200) {
                const customer = res.data;
                if (customer) {
                  setInitialCustomerValues({
                    firstName: customer?.firstName || "",
                    lastName: customer?.lastName || "",
                    phoneNumber: customer?.phoneNumber || "",
                    email: customer?.email || "",
                    address1: customer?.address1 || "",
                    address2: customer?.address2 || "",
                    address3: customer?.address3 || "",
                    cityOrLocality: customer?.cityOrLocality || "",
                    stateOrProvinceCode: customer?.stateOrProvinceCode || "",
                    postalCodeOrZip: customer?.postalCodeOrZip || "",
                    smsConsent: customer?.smsConsent ? true : false,
                  });
                  if (customer?.stateOrProvinceCode) {
                    fetchCitiesByState(customer.stateOrProvinceCode);
                  }
                }
              } else {
                showErrorMessage(t("error_fetch_customer"));
              }
            })
            .catch((err) => {
              showErrorMessage(err.message);
            })
            .finally(() => {
              hideLoader();
            }),
        );
      }
    }
    if (state.goToVehicleStep || state.goToCustomerStep) {
      if (state.goToVehicleStep) {
        setTimeout(() => {
          stepper.current.to(2);
        });
        setCompletedSteps([0]);
      }
      if (vehicleId) {
        const idData = {
          id: vehicleId,
        };
        showLoader();
        promises.push(
          apiManager
            .post(API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID, idData)
            .then((res) => {
              const vehicle = res.data;
              if (vehicle) {
                setVehicleEditData(vehicle);
                setInitialVehicleValues({
                  year: vehicle?.year || "",
                  make: vehicle?.makeName || "",
                  model: vehicle?.modelName || "",
                  submodel: vehicle?.subModelId || "",
                  vin: vehicle?.vin || "",
                });
              }
            })
            .catch((err) => {
              showErrorMessage(err);
            })
            .finally(() => {
              hideLoader();
            }),
        );
      }
    }

    Promise.all(promises).finally(() => {});
    fetchYears();
  }, []);

  const fetchCitiesByState = async (stateCode) => {
    if (!stateCode) {
      setAvailableCities([]);
      return;
    }
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.DROPDOWN_CITIES_BY_STATE,
        { stateCode },
      );
      if (response.status === 200) {
        setAvailableCities(response.data || []);
      } else {
        showErrorMessage(t("error_fetch_cities"));
        setAvailableCities([]);
      }
    } catch (error) {
      showErrorMessage(error.message || t("error_fetching_cities"));
      setAvailableCities([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (!selectedYear || isVinMode) return;
    const payload = { year: selectedYear.toString() };
    showLoader();
    apiManager
      .post(API_ENDPOINTS.CUSTOMER_GET_YEAR_MAKES, payload)
      .then((response) => {
        const data = response.data;
        if (Array.isArray(data)) {
          const mappedMakes = data.map((item) => ({
            ...item,
            makeID: item.makeID ? Number(item.makeID) : null,
          }));
          setMakes(mappedMakes);
        }
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        hideLoader();
      });
  }, [selectedYear, isVinMode]);

  useEffect(() => {
    if (selectedMake && !isVinMode) {
      const payload = {
        year: selectedYear.toString(),
        makeId: selectedMakeId.toString(),
      };
      showLoader();
      apiManager
        .post(API_ENDPOINTS.CUSTOMER_GET_YEAR_MOTOR, payload)
        .then((response) => {
          const data = response.data;
          setModels(data);
        })
        .catch((err) => {
          showErrorMessage(err);
        })
        .finally(() => {
          hideLoader();
        });
    }
  }, [selectedMake]);

  useEffect(() => {
    if (selectedMake && selectedYear && selectedModelId && !isVinMode) {
      const payload = {
        year: selectedYear.toString(),
        makeId: selectedMakeId.toString(),
        modelId: selectedModelId.toString(),
      };
      apiManager
        .post(API_ENDPOINTS.CUSTOMER_GET_SUBMODELS_MOTOR, payload)
        .then((response) => {
          const data = response.data;
          setSubModels(data);
        })
        .catch((err) => {
          showErrorMessage(err);
        });
    }
  }, [selectedMake, selectedYear, selectedModelId, isVinMode]);

  useEffect(() => {
    if (
      selectedYear &&
      selectedMakeId &&
      selectedModelId &&
      selectedSubModelId &&
      !isVinMode
    ) {
      (async () => {
        const engineBody = await engineBodyData(
          selectedYear,
          selectedMakeId,
          selectedModelId,
          selectedSubModelId,
        );
        setVehicleEditData((prev) => ({
          ...prev,
          engine: engineBody.Engine || "",
          body: engineBody.Body || "",
        }));
      })();
    }
  }, [
    selectedYear,
    selectedMakeId,
    selectedModelId,
    selectedSubModelId,
    isVinMode,
  ]);

  useEffect(() => {
    if (
      !isVehicleEditMode ||
      !Array.isArray(submodels) ||
      submodels.length === 0 ||
      !vehicleEditData
    )
      return;
    const rawSubModelId =
      vehicleEditData.subModelId ??
      vehicleEditData.subModelID ??
      vehicleEditData.subModelid ??
      vehicleEditData.subModel;
    if (
      typeof rawSubModelId === "undefined" ||
      rawSubModelId === null ||
      rawSubModelId === ""
    )
      return;
    const subModelIdNum = Number(rawSubModelId);
    const found = submodels.find(
      (sm) =>
        Number(sm.subModelID ?? sm.subModelId ?? sm.subModel) === subModelIdNum,
    );
    if (found) {
      setInitialVehicleValues((prev) => ({
        ...prev,
        submodel: subModelIdNum,
      }));
      setSelectedSubModelId(String(subModelIdNum));
    }
  }, [submodels, isVehicleEditMode, vehicleEditData]);

  useEffect(() => {
    if (vehicleEditData && !isVinMode) {
      setSelectedYear(vehicleEditData.year || "");
      const makeId =
        vehicleEditData.makeId ??
        vehicleEditData.makeID ??
        vehicleEditData.makeid;
      const modelId =
        vehicleEditData.modelId ??
        vehicleEditData.modelID ??
        vehicleEditData.modelid;
      const subModelId =
        vehicleEditData.subModelId ??
        vehicleEditData.subModelID ??
        vehicleEditData.subModelid ??
        vehicleEditData.subModel;
      if (makeId !== undefined && makeId !== null)
        setSelectedMakeId(String(makeId));
      setSelectedMake(vehicleEditData.makeName || "");
      if (modelId !== undefined && modelId !== null)
        setSelectedModelId(String(modelId));
      if (subModelId !== undefined && subModelId !== null)
        setSelectedSubModelId(String(subModelId));
    }
  }, [vehicleEditData, isVinMode]);

  const populateVehicleData = () => {
    if (vehicleId) {
      showLoader();
      apiManager
        .post(API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID, { id: vehicleId })
        .then((res) => {
          const vehicle = res.data;
          if (vehicle) {
            setVehicleEditData(vehicle);
            setInitialVehicleValues({
              year: vehicle?.year || "",
              make: vehicle?.makeName || "",
              model: vehicle?.modelName || "",
              submodel: vehicle?.subModelId || "",
              vin: vehicle?.vin || "",
            });
          }
        })
        .catch((err) => {
          showErrorMessage(err);
        })
        .finally(() => {
          hideLoader();
        });
    } else {
      setVehicleEditData({});
    }
  };

  const fetchYears = async () => {
    try {
      showLoader();
      const res = await apiManager.get(
        API_ENDPOINTS.API_CUSTOMER_GET_YEAR_MOTOR,
      );
      if (res.status == 200) {
        const parsed = res.data;
        if (Array.isArray(parsed)) {
          setYears(parsed);
        }
      } else {
        showErrorMessage(t("error_fetch_years"));
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      hideLoader();
    }
  };

  const engineBodyData = async (year, makeId, modelId, subModelId) => {
    const payload = {
      year: year ? Number(year) : null,
      makeId: makeId ? Number(makeId) : null,
      modelId: modelId ? Number(modelId) : null,
      submodelId: subModelId ? Number(subModelId) : null,
    };
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_ENGINE_BODY,
        payload,
      );
      if (
        response.status === 200 &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        return response.data[0];
      } else {
        showErrorMessage(t("error_fetch_engine_body"));
        return {};
      }
    } catch (err) {
      showErrorMessage(err);
      return {};
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (
      selectedYear &&
      selectedMakeId &&
      selectedModelId &&
      selectedSubModelId &&
      !isVinMode
    ) {
      (async () => {
        const engineBody = await engineBodyData(
          selectedYear,
          selectedMakeId,
          selectedModelId,
          selectedSubModelId,
        );
        setVehicleEditData((prev) => ({
          ...prev,
          engine: engineBody.Engine || "",
          body: engineBody.Body || "",
        }));
      })();
    }
  }, [selectedYear, selectedMakeId, selectedModelId, isVinMode]);

  const fetchVinDetails = async (vin, setFieldValue) => {
    setHasVehicleChanges(true);
    try {
      const payload = { vin };
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_VIN,
        payload,
      );
      if (response.status == 200) {
        const data = response.data;
        setVinError(false);
        if (data) {
          const {
            year,
            makeID,
            makeName,
            modelName,
            modelID,
            subModelID,
            subModelName,
          } = data;
          setIsVinMode(true);
          setYears([{ year }]);
          setSelectedYear(year);
          setFieldValue("year", year);
          setMakes([{ makeID, make: makeName }]);
          setSelectedMake(makeName);
          setSelectedMakeId(makeID.toString());
          setFieldValue("make", makeName);
          setModels([{ modelID, model: modelName }]);
          setSelectedModelId(modelID);
          setFieldValue("model", modelName);
          setSubModels([
            { subModelID: Number(subModelID), subModelName: subModelName },
          ]);
          setSelectedSubModelId(subModelID);
          setFieldValue("submodel", Number(subModelID));
          setVehicleEditData((prev) => ({
            ...prev,
            subModelName: subModelName || "",
          }));
          let submodelID = Number(subModelID);
          const engineBody = await engineBodyData(
            year,
            makeID,
            Number(modelID),
            submodelID,
          );
          setVehicleEditData((prev) => ({
            ...prev,
            engine: engineBody.engine || "",
            body: engineBody.body || "",
          }));
        }
      } else {
        showErrorMessage(t("error_fetch_vin_details"));
      }
    } catch (error) {
      showErrorMessage(error);
      setVinError(true);
    }
  };

  const next = () => stepper.current.next();
  const previous = () => stepper.current.previous();
  const steps = [
    {
      id: "step1",
      label: isCustomerEditMode
        ? t("edit_customer_title")
        : t("create_customer_title"),
    },
    { id: "step2", label: t("step_select_vehicle") },
    { id: "step3", label: t("step_select_services") },
  ];

  const updateCustomer = async (customerData) => {
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_UPDATE,
        customerData,
      );
      showSuccessMessage(t("customer_updated"));
      return response.data;
    } catch (error) {
      showErrorMessage(error.message || error);
      throw error;
    } finally {
      hideLoader();
    }
  };

  const addCustomer = async (customerData) => {
    try {
      showLoader();
      customerData = {
        ...customerData,
        tenantId: tenantId,
        dealershipId: selectedDealershipId,
      };
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_CREATE,
        customerData,
      );
      showSuccessMessage(t("customer_created"));
      let customerResponse = null;
      customerResponse = response.data;
      return customerResponse;
    } catch (error) {
      showErrorMessage(error.message || error);
      throw error;
    } finally {
      hideLoader();
    }
  };

  const addVehicle = async (vehicleData) => {
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_CREATE_VEHICLE,
        vehicleData,
      );
      let vehicleResponse = null;
      vehicleResponse = response.data;
      showSuccessMessage(t("vehicle_created"));
      return vehicleResponse;
    } catch (error) {
      throw error;
    } finally {
      hideLoader();
    }
  };

  const handleYearChange = async (year, setFieldValue) => {
    setFieldValue("vin", "");
    setHasVehicleChanges(true);
    setFieldValue("year", year);
    setMakes([]);
    setModels([]);
    setSubModels([]);
    if (!year || isVinMode) return;
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_YEAR_MAKES,
        { year },
      );
      setMakes(
        response.data.map((item) => ({
          ...item,
          makeID: item.makeID.toString(),
        })),
      );
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const handleMakeChange = async (makeName, year, setFieldValue) => {
    setFieldValue("vin", "");
    setHasVehicleChanges(true);
    setFieldValue("make", makeName);
    setModels([]);
    setSubModels([]);
    if (!makeName || !year || isVinMode) return;
    const selectedMake = makes.find((m) => m.make === makeName);
    const payload = {
      year: year.toString(),
      makeId: selectedMake.makeID.toString(),
    };
    if (!selectedMake) return;
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_YEAR_MOTOR,
        payload,
      );
      setModels(response.data);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const handleModelChange = async (
    modelName,
    year,
    makeName,
    setFieldValue,
  ) => {
    setFieldValue("vin", "");
    setHasVehicleChanges(true);
    setFieldValue("model", modelName);
    setSubModels([]);
    if (!modelName || !year || !makeName || isVinMode) return;
    const selectedMake = makes.find((m) => m.make === makeName);
    const selectedModel = models.find((m) => m.model === modelName);
    if (!selectedMake || !selectedModel) return;
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_SUBMODELS_MOTOR,
        {
          year,
          makeId: selectedMake.makeID,
          modelId: selectedModel.modelID,
        },
      );
      setSubModels(response.data);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const updateVehicle = async (vehicleData) => {
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_UPDATE_VEHICLE,
        vehicleData,
      );
      showSuccessMessage(t("vehicle_updated"));
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      hideLoader();
    }
  };

  const handleSaveCustomerAndVehicle = async (vehicleValues, setSubmitting) => {
    setHasVehicleChanges(true);
    try {
      const selectedMake = makes.find((m) => m.make === vehicleValues.make);
      const selectedModel = models.find((m) => m.model === vehicleValues.model);
      const selectedSubModelId = vehicleValues.submodel;
      const year = vehicleValues.year ? Number(vehicleValues.year) : null;
      let subModelName = "";
      if (
        Array.isArray(submodels) &&
        submodels.length > 0 &&
        selectedSubModelId !== ""
      ) {
        const foundSubModel = submodels.find(
          (sm) =>
            String(sm.subModelID) === String(selectedSubModelId) ||
            Number(sm.subModelID) === Number(selectedSubModelId),
        );
        if (foundSubModel) {
          subModelName = foundSubModel.subModelName || "";
        }
      }

      if (!isVinMode) {
        if (!selectedMake || !selectedModel || !selectedSubModelId || !year) {
          showErrorMessage(t("error_select_all_vehicle_details"));
          setSubmitting(false);
          return;
        }
      } else {
        if (!vehicleEditData) {
          if (!selectedMake || !selectedModel || !selectedSubModelId || !year) {
            showErrorMessage(t("error_select_all_vehicle_details"));
            setSubmitting(false);
            return;
          }
        }
      }
      let engine = vehicleEditData?.engine;
      let body = vehicleEditData?.body;
      if (!engine || !body) {
        const engineBody = await engineBodyData(
          year,
          selectedMake.makeID,
          selectedModel.modelID,
          selectedSubModelId,
        );
        engine = engineBody.engine || "";
        body = engineBody.body || "";
        setVehicleEditData((prev) => ({ ...prev, engine, body }));
      }
      let savedCustomer;
      if (isCustomerEditMode) {
        savedCustomer = await updateCustomer({
          ...formData.customer,
          id: customerId,
          smsConsent: formData.customer.smsConsent,
        });
      } else if (!isCustomerEditMode && !isVehicleEditMode) {
        savedCustomer = await addCustomer({
          ...formData.customer,
          smsConsent: formData.customer.smsConsent,
        });
      }
      let vehiclePayload;
      if (isVehicleEditMode || vehicleId) {
        vehiclePayload = {
          ...vehicleValues,
          year,
          customerId: customerId,
          makeName: vehicleValues.make,
          modelName: vehicleValues.model,
          makeId: selectedMake.makeID,
          modelId: selectedModel.modelID,
          subModelId: selectedSubModelId,
          subModelName,
          vinLast6Digit: vehicleValues.vin?.slice(-6) || null,
          vin: vehicleValues.vin || null,
          engine,
          body,
        };
      } else {
        setVehicleId(vehicleId);
        vehiclePayload = {
          ...vehicleValues,
          year,
          customerId: savedCustomer.data,
          makeName: vehicleValues.make,
          modelName: vehicleValues.model,
          makeId: selectedMake.makeID,
          modelId: selectedModel.modelID,
          subModelId: selectedSubModelId,
          subModelName,
          vinLast6Digit: vehicleValues.vin?.slice(-6) || null,
          vin: vehicleValues.vin || null,
          engine,
          body,
        };
      }
      let savedVehicle;

      if (vehicleId) {
        if (isVehicleEditMode && hasVehicleChanges) {
          await updateVehicle({ ...vehiclePayload, id: vehicleId });
        } else {
        }
      } else {
        savedVehicle = await addVehicle(vehiclePayload);
      }

      const fullCustomerId =
        isVehicleEditMode || vehicleId ? customerId : savedCustomer.data;
      const fullVehicleId =
        isVehicleEditMode || vehicleId
          ? vehicleId
          : savedVehicle?.data || vehicleId;
      const customerRes = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_BY_ID,
        { id: fullCustomerId },
      );
      const vehicleRes = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
        { id: fullVehicleId },
      );
      const fullCustomer = customerRes.data;
      const fullVehicle = vehicleRes.data;
      const patchedVehicle = {
        ...fullVehicle,
        engine: vehiclePayload.engine || "",
        body: vehiclePayload.body || "",
        subModelId:
          fullVehicle.subModelId ||
          selectedSubModelId ||
          vehicleEditData?.subModelId ||
          "",
        subModelName:
          fullVehicle.subModelName ||
          vehicleEditData?.subModelName ||
          (Array.isArray(submodels)
            ? submodels.find(
                (sm) => Number(sm.subModelID) === Number(selectedSubModelId),
              )?.subModelName || ""
            : ""),
      };
      setFormData({
        customer: fullCustomer,
        vehicle: patchedVehicle,
      });
      setCompletedSteps((prev) => [...new Set([...prev, 1])]);
      navigate("/second-step", {
        state: {
          goToStep2: true,
          customerId: fullCustomer.id,
          customer: fullCustomer,
          vehicleId: patchedVehicle.id,
          vehicle: patchedVehicle,
        },
      });
    } catch (error) {
      showErrorMessage(error);
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleCancelClose = () => {
    setShowCancelModal(false);
  };

  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    const userRole = getUserRoleFromJWT();
    navigate(
      userRole === UserRole.DEALER_ADMIN
        ? "/admin-dashboard"
        : "/service-desk-dashboard",
    );
  };

  const fields = [
    {
      name: "firstName",
      label: t("label_first_name"),
      placeholder: t("placeholder_first_name"),
      required: true,
    },
    {
      name: "lastName",
      label: t("label_last_name"),
      placeholder: t("placeholder_last_name"),
      required: true,
    },
    {
      name: "phoneNumber",
      label: t("label_phone"),
      placeholder: t("placeholder_phone"),
      required: true,
    },
    {
      name: "email",
      label: t("label_email"),
      placeholder: t("placeholder_email"),
      required: true,
    },
    {
      name: "address1",
      label: t("label_address1"),
      placeholder: t("placeholder_address1"),
      required: true,
    },
    {
      name: "address2",
      label: t("label_address2"),
      placeholder: t("placeholder_address2"),
      required: false,
    },
    {
      name: "address3",
      label: t("label_address3"),
      placeholder: t("placeholder_address3"),
      required: false,
    },
    {
      name: "stateOrProvinceCode",
      label: t("label_state_province"),
      placeholder: t("placeholder_state"),
      required: true,
    },
    {
      name: "cityOrLocality",
      label: t("label_city"),
      placeholder: t("placeholder_city"),
      required: true,
    },
    {
      name: "postalCodeOrZip",
      label: t("label_postal_zip"),
      placeholder: t("placeholder_postal_zip"),
      required: true,
    },
  ];

  return (
    <div className="container content-wrapper">
      <style>
        {`
          .step-completed .bs-stepper-circle {
            background-color: #28a745 !important;
            color: white;
            border-color: #28a745;
          }

          .sms-consent-check {
            width: 1.1rem;
            height: 1.1rem;
          }

          .sms-consent-label {
            font-size: 0.95rem;
            font-weight: 500;
            margin-left: 0.3rem;
          }
        `}
      </style>
      <div id="stepper" ref={stepperRef} className="bs-stepper">
        <div
          className="bs-stepper-header justify-content-center"
          role="tablist"
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className={`step ${
                  completedSteps.includes(index) ? "step-completed" : ""
                }`}
                data-target={`#${step.id}`}
              >
                <button
                  type="button"
                  className="step-trigger"
                  role="tab"
                  aria-controls={step.id}
                  id={`stepper-trigger-${index + 1}`}
                  onClick={(e) => {
                    const maxAllowed = completedSteps.length
                      ? Math.max(...completedSteps) + 1
                      : 0;
                    if (index > maxAllowed) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="bs-stepper-circle">{index + 1}</span>
                  <span className="bs-stepper-label">{step.label}</span>
                </button>
              </div>
              {index < steps.length - 1 && <div className="line" />}
            </React.Fragment>
          ))}
        </div>
        <div className="bs-stepper-content d-flex justify-content-center">
          <div
            id="step1"
            className="content select-vehicle-content mb-4"
            role="tabpanel"
            aria-labelledby="stepper-trigger-1"
          >
            <div
              className="form-card col-md-12 mx-auto"
              style={{ maxWidth: "1000px" }}
            >
              <h5 className="text-center mb-3 mt-3">
                {isCustomerEditMode
                  ? t("edit_customer_title")
                  : t("create_customer_title")}
              </h5>
              <Formik
                initialValues={initialCustomerValues}
                enableReinitialize={true}
                validationSchema={Yup.object().shape({
                  firstName: Yup.string()
                    .required(t("validation_first_name_required"))
                    .matches(
                      /^[A-Za-z.'-\s]+$/,
                      t("validation_first_name_pattern"),
                    )
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_no_leading_trailing_spaces_First_Name"),
                      (v) => v === (v || "").trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_no_multi_spaces_First_Name"),
                      (v) => !/\s{2,}/.test(v || ""),
                    )
                    .min(2, t("validation_first_name_min"))
                    .max(50, t("validation_first_name_max")),

                  lastName: Yup.string()
                    .required(t("validation_last_name_required"))
                    .matches(
                      /^[A-Za-z.'-\s]+$/,
                      t("validation_last_name_pattern"),
                    )
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_no_leading_trailing_spaces_Last_Name"),
                      (v) => v === (v || "").trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_no_multi_spaces_Last_Name"),
                      (v) => !/\s{2,}/.test(v || ""),
                    )
                    .min(2, t("validation_last_name_min"))
                    .max(50, t("validation_last_name_max")),

                  phoneNumber: Yup.string()
                    .required(t("validation_phone_required"))
                    .test(
                      "exact-10-digits",
                      t("validation_phone_invalid"),
                      (value) => {
                        if (!value) return false;
                        const digitsOnly = value.replace(/\D/g, "");
                        return digitsOnly.length === 11;
                      },
                    ),
                  email: Yup.string()
                    .required(t("validation_email_required"))
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_email_spaces"),
                      (value) => value === value?.trim(),
                    )
                    .matches(
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      t("validation_email_invalid"),
                    ),
                  address1: Yup.string()
                    .required(t("validation_address1_required"))
                    .matches(
                      /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
                      t("validation_address1_invalid_chars"),
                    )
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_address1_no_leading_trailing_spaces"),
                      (v) => v === (v || "").trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_address1_no_multi_spaces"),
                      (v) => !/\s{2,}/.test(v || ""),
                    )
                    .test(
                      "not-only-numbers",
                      t("validation_address1_no_only_numbers"),
                      (v) => !/^\d+$/.test(v || ""),
                    )
                    .min(5, t("validation_address1_min_length"))
                    .max(50, t("validation_address1_max_length")),

                  address2: Yup.string()
                    .notRequired()
                    .matches(
                      /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
                      t("validation_address2_invalid_chars"),
                    )
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_address2_no_leading_trailing_spaces"),
                      (v) => !v || v === v.trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_address2_no_multi_spaces"),
                      (v) => !v || !/\s{2,}/.test(v),
                    )
                    .test(
                      "not-only-numbers",
                      t("validation_address2_no_only_numbers"),
                      (v) => !v || !/^\d+$/.test(v),
                    )
                    .min(5, t("validation_address2_min_length"))
                    .max(50, t("validation_address2_max_length")),

                  address3: Yup.string()
                    .notRequired()
                    .matches(
                      /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
                      t("validation_address3_invalid_chars"),
                    )
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_address3_no_leading_trailing_spaces"),
                      (v) => !v || v === v.trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_address3_no_multi_spaces"),
                      (v) => !v || !/\s{2,}/.test(v),
                    )
                    .test(
                      "not-only-numbers",
                      t("validation_address3_no_only_numbers"),
                      (v) => !v || !/^\d+$/.test(v),
                    )
                    .min(5, t("validation_address3_min_length"))
                    .max(50, t("validation_address3_max_length")),
                  cityOrLocality: Yup.string().required(
                    t("validation_city_required"),
                  ),
                  stateOrProvinceCode: Yup.string()
                    .test(
                      "no-leading-trailing-spaces",
                      t("validation_no_leading_trailing_spaces"),
                      (v) => v === (v || "").trim(),
                    )
                    .test(
                      "no-multi-spaces",
                      t("validation_state_province_no_multi_spaces"),
                      (v) => !/\s{2,}/.test(v || ""),
                    )
                    .required(t("validation_state_province_required")),
                  postalCodeOrZip: Yup.string()
                    .required(t("validation_postal_zip_required"))
                    .matches(
                      /^\d{5}(-\d{4})?$/,
                      t("validation_postal_zip_pattern"),
                    ),
                  smsConsent: Yup.boolean(),
                })}
                onSubmit={(values) => {
                  setFormData((prev) => ({ ...prev, customer: values }));
                  setCompletedSteps((prev) => [...new Set([...prev, 0])]);
                  next();
                }}
              >
                {({ resetForm, touched, errors, values, setFieldValue }) => (
                  <Form>
                    <div className="row card-body">
                      {fields.map(({ name, label, placeholder, required }) => (
                        <div className="form-group col-md-6 mb-3" key={name}>
                          <FormLabel
                            htmlFor={name}
                            label={label}
                            required={required}
                          />
                          {name === "stateOrProvinceCode" ? (
                            <div className="dropdown-container">
                              <Select
                                name={name}
                                options={US_STATES.map((state) => ({
                                  value: state.code,
                                  label: `${state.name} (${state.code})`,
                                }))}
                                classNamePrefix="select"
                                className={
                                  touched.stateOrProvinceCode &&
                                  errors.stateOrProvinceCode
                                    ? "is-invalid"
                                    : ""
                                }
                                value={
                                  US_STATES.find(
                                    (opt) =>
                                      opt.code === values.stateOrProvinceCode,
                                  )
                                    ? {
                                        value: values.stateOrProvinceCode,
                                        label: `${
                                          US_STATES.find(
                                            (opt) =>
                                              opt.code ===
                                              values.stateOrProvinceCode,
                                          ).name
                                        } (${values.stateOrProvinceCode})`,
                                      }
                                    : null
                                }
                                onChange={(opt) => {
                                  setFieldValue(name, opt ? opt.value : "");
                                  fetchCitiesByState(opt ? opt.value : "");
                                  setFieldValue("cityOrLocality", "");
                                }}
                                placeholder="Select State"
                                isClearable
                              />
                            </div>
                          ) : name === "cityOrLocality" ? (
                            <div className="dropdown-container">
                              <Select
                                name={name}
                                options={availableCities.map((city) => ({
                                  value: city,
                                  label: city,
                                }))}
                                classNamePrefix="select"
                                className={
                                  touched.cityOrLocality &&
                                  errors.cityOrLocality
                                    ? "is-invalid"
                                    : ""
                                }
                                value={
                                  availableCities.find(
                                    (city) => city === values.cityOrLocality,
                                  )
                                    ? {
                                        value: values.cityOrLocality,
                                        label: values.cityOrLocality,
                                      }
                                    : null
                                }
                                onChange={(opt) =>
                                  setFieldValue(
                                    "cityOrLocality",
                                    opt ? opt.value : "",
                                  )
                                }
                                placeholder="Select City"
                                isClearable
                                isDisabled={availableCities.length === 0}
                              />
                            </div>
                          ) : name === "phoneNumber" ? (
                            <Field
                              id={name}
                              name={name}
                              render={({ field, form }) => (
                                <PhoneInput
                                  {...field}
                                  placeholder={placeholder}
                                  country="US"
                                  countries={["US"]}
                                  addInternationalOption={false}
                                  international={false}
                                  onChange={(value) =>
                                    form.setFieldValue(name, value)
                                  }
                                  className="form-control"
                                />
                              )}
                            />
                          ) : name === "postalCodeOrZip" ? (
                            <Field
                              id={name}
                              name={name}
                              placeholder={placeholder}
                              className="form-control"
                              onChange={(e) => {
                                const formattedValue = formatZipCode(
                                  e.target.value,
                                );
                                setFieldValue(name, formattedValue);
                              }}
                            />
                          ) : (
                            <Field
                              id={name}
                              name={name}
                              placeholder={placeholder}
                              className="form-control"
                            />
                          )}
                          <div className="text-danger small">
                            <ErrorMessage name={name} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="d-flex justify-content-center mb-2">
                      <div className="form-check">
                        <Field
                          type="checkbox"
                          id="smsConsent"
                          name="smsConsent"
                          className="form-check-input sms-consent-check"
                        />
                        <label
                          className="form-check-label sms-consent-label"
                          htmlFor="smsConsent"
                        >
                          I consent to receive SMS on the mobile number
                          provided.
                        </label>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center gap-2 card-footer">
                      <button
                        onClick={populateVehicleData}
                        type="submit"
                        className="btn btn-success"
                      >
                        {t("next")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={handleCancel}
                      >
                        {t("cancel")}
                      </button>
                    </div>
                    {showCancelModal && (
                      <div
                        className="modal fade show"
                        style={{ display: "block" }}
                        tabIndex="-1"
                        role="dialog"
                      >
                        <div
                          className="modal-dialog modal-dialog-centered"
                          role="document"
                        >
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">
                                {t("cancel_confirmation_title")}
                              </h5>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-success me-2"
                                onClick={handleCancelConfirm}
                              >
                                {t("yes_go_dashboard")}
                              </button>
                              <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={handleCancelClose}
                              >
                                {t("no")}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div
                          className="modal-backdrop fade show"
                          style={{ zIndex: 1040 }}
                        ></div>
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          {/* STEP 2: Vehicle */}
          <div
            id="step2"
            className="content select-vehicle-content"
            role="tabpanel"
            aria-labelledby="stepper-trigger-2"
          >
            <div className="form-card" style={{ width: "600px" }}>
              <h5 className="mb-2 text-center mt-3">{t("select_vehicle")}</h5>
              <Formik
                initialValues={initialVehicleValues}
                enableReinitialize={true}
                validationSchema={(() => {
                  if (isVinMode) {
                    return Yup.object().shape({
                      year: Yup.string()
                        .notRequired()
                        .nullable()
                        .matches(/^\d{4}$/, "Year must be 4 digits")
                        .transform((v) => (v === "" ? null : v)),
                      make: Yup.string().notRequired(),
                      model: Yup.string().notRequired(),
                      vin: Yup.string()
                        .min(17, "VIN must be of 17 characters")
                        .max(17, "VIN must be of 17 characters")
                        .notRequired(),
                    });
                  }
                  return Yup.object().shape({
                    year: Yup.string()
                      .required("Year is required")
                      .matches(/^\d{4}$/, "Year must be 4 digits"),
                    make: Yup.string().required("Make is required"),
                    model: Yup.string().required("Model is required"),
                    vin: Yup.string()
                      .min(17, "VIN must be of 17 characters")
                      .max(17, "VIN must be of 17 characters"),
                  });
                })()}
                onSubmit={(values, { setSubmitting }) => {
                  handleSaveCustomerAndVehicle(values, setSubmitting);
                }}
              >
                {({
                  values,
                  setFieldValue,
                  resetForm,
                  dirty,
                  isValid,
                  isSubmitting,
                }) => (
                  <Form>
                    {/* Year + VIN */}
                    <div className="add-new-vehicle-page">
                      <div className="card-body">
                        <div className="container">
                          <div className="row">
                            <div className="col-md-5 left-panel">
                              <div className="form-group mb-3">
                                <FormLabel
                                  htmlFor="year"
                                  label={t("year")}
                                  required={true}
                                  className="form-label"
                                />
                                <Field
                                  disabled={isVinMode}
                                  as="select"
                                  name="year"
                                  className="form-control form-select"
                                  onChange={(e) =>
                                    handleYearChange(
                                      e.target.value,
                                      setFieldValue,
                                    )
                                  }
                                >
                                  <option value="">{t("select_year")}</option>
                                  {Array.isArray(years) &&
                                    years.map((item) => (
                                      <option key={item.year} value={item.year}>
                                        {item.year}
                                      </option>
                                    ))}
                                </Field>
                                <div className="text-danger small">
                                  <ErrorMessage name="year" />
                                </div>
                              </div>
                              <div className="form-group mb-3">
                                <FormLabel
                                  htmlFor="make"
                                  label={t("make")}
                                  required={true}
                                  className="form-label"
                                />
                                <Field
                                  as="select"
                                  name="make"
                                  className="form-control form-select"
                                  disabled={isVinMode}
                                  onChange={(e) =>
                                    handleMakeChange(
                                      e.target.value,
                                      values.year,
                                      setFieldValue,
                                    )
                                  }
                                >
                                  <option value="">Select Make</option>
                                  {Array.isArray(makes) &&
                                    makes.map((item) => (
                                      <option key={item.make} value={item.make}>
                                        {item.make}
                                      </option>
                                    ))}
                                </Field>
                                <div className="text-danger small">
                                  <ErrorMessage name="make" />
                                </div>
                              </div>

                              <div className="form-group mb-3">
                                <FormLabel
                                  htmlFor="model"
                                  label={t("model")}
                                  required={true}
                                  className="form-group"
                                />
                                <Field
                                  disabled={isVinMode}
                                  as="select"
                                  name="model"
                                  className="form-control form-select"
                                  onChange={(e) =>
                                    handleModelChange(
                                      e.target.value,
                                      values.year,
                                      values.make,
                                      setFieldValue,
                                    )
                                  }
                                >
                                  <option value="">Select Model</option>
                                  {Array.isArray(models) &&
                                    models.map((item) => (
                                      <option
                                        key={item.model}
                                        value={item.model}
                                      >
                                        {item.model}
                                      </option>
                                    ))}
                                </Field>
                                <div className="text-danger small">
                                  <ErrorMessage name="model" />
                                </div>
                              </div>
                              <div className="form-group mb-3">
                                <FormLabel
                                  htmlFor="submodel"
                                  label={t("sub_model")}
                                  required={true}
                                  className="form-group row mb-4"
                                />
                                <Field
                                  disabled={isVinMode}
                                  as="select"
                                  name="submodel"
                                  className="form-control form-select"
                                  onChange={(e) => {
                                    setFieldValue(
                                      "submodel",
                                      Number(e.target.value),
                                    );
                                    setFieldValue("vin", "");
                                    setHasVehicleChanges(true);
                                  }}
                                >
                                  <option value="">Select SubModel</option>
                                  {Array.isArray(submodels) &&
                                    submodels.map((item) => (
                                      <option
                                        key={item.subModelID}
                                        value={item.subModelID}
                                      >
                                        {item.subModelName}
                                      </option>
                                    ))}
                                </Field>
                                <div className="text-danger small">
                                  <ErrorMessage name="submodel" />
                                </div>
                              </div>
                            </div>
                            <div className="col-md-2 d-flex justify-content-center align-items-center middle-panel">
                              <div className="or-text">
                                <span>{t("or")}</span>
                              </div>
                            </div>
                            <div className="col-md-5 d-flex align-items-center right-panel">
                              <div className="form-group w-100 mb-3">
                                <FormLabel
                                  htmlFor="vin"
                                  label={t("vin")}
                                  required={false}
                                  className="form-label text-end"
                                />
                                <div className="input-group input-group-sm">
                                  <Field
                                    name="vin"
                                    type="text"
                                    className="form-control"
                                    placeholder="VIN"
                                    onChange={(e) => {
                                      const newVin = e.target.value;
                                      setFieldValue("vin", newVin);
                                      if (vinError) {
                                        setVinError(false);
                                      }
                                      if (newVin.trim() !== "") {
                                        setIsVinMode(true);
                                        setFieldValue("year", "");
                                        setFieldValue("make", "");
                                        setFieldValue("model", "");
                                        setFieldValue("submodel", "");
                                        setYears([]);
                                        setMakes([]);
                                        setModels([]);
                                        setSubModels([]);
                                      } else {
                                        setIsVinMode(false);
                                        setFieldValue("year", "");
                                        setSelectedYear("");
                                        setMakes([]);
                                        setModels([]);
                                        setSubModels([]);
                                        fetchYears();
                                      }
                                    }}
                                  />
                                  <button
                                    className="btn btn-success"
                                    type="button"
                                    onClick={async () => {
                                      if (
                                        values.vin &&
                                        values.vin.length === 17
                                      ) {
                                        await fetchVinDetails(
                                          values.vin,
                                          setFieldValue,
                                        );
                                      }
                                    }}
                                    disabled={
                                      !values.vin || values.vin.length !== 17
                                    }
                                  >
                                    <i class="fas fa-search"></i>
                                  </button>
                                </div>
                                <div className="text-danger small">
                                  <ErrorMessage name="vin" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-center card-footer gap-3">
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={previous}
                        >
                          {t("back")}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={vinError}
                        >
                          {t("save")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-cancel"
                          onClick={() => {
                            navigate("/second-step");
                          }}
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div
            id="step3"
            className="content"
            role="tabpanel"
            aria-labelledby="stepper-trigger-3"
          ></div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomerPage;
